import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Toaster, toast } from '../components/ui/sonner';
import { 
  Code, Smartphone, Database, Cog, Plug, Box, Palette, Headphones,
  Users, Target, Award, Clock, Menu, X, MessageCircle, CheckCircle,
  ArrowRight, Star, Zap, Shield, Rocket, Loader2
} from 'lucide-react';
import { mockData } from '../mock';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const subject = encodeURIComponent(`Solicitud de cotización - ${formData.name}`);
      const body = encodeURIComponent(
        [
          'Hola,',
          '',
          'Quiero solicitar una cotización con estos datos:',
          '',
          `Nombre: ${formData.name}`,
          `Email: ${formData.email}`,
          `Teléfono: ${formData.phone}`,
          '',
          'Proyecto:',
          formData.message,
          '',
          'Gracias.'
        ].join('\n')
      );
      const mailtoLink = `mailto:${mockData.contactEmail}?subject=${subject}&body=${body}`;

      window.location.href = mailtoLink;

      toast.success('Se abrirá tu correo con la solicitud prellenada.', {
        description: `Solo revisa y envíala a ${mockData.contactEmail}.`
      });

      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('No se pudo abrir tu correo', {
        description: 'Intenta de nuevo o contáctanos por WhatsApp.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className={`navbar ${scrollY > 50 ? 'navbar-scrolled' : ''}`}>
        <div className="container-custom">
          <div className="navbar-content">
            <div className="logo-container">
              <Code className="logo-icon" />
              <span className="logo-text">Código a la Medida</span>
            </div>
            
            <div className={`nav-links ${isMenuOpen ? 'nav-links-open' : ''}`}>
              <button onClick={() => scrollToSection('servicios')} className="nav-link">Servicios</button>
              <button onClick={() => scrollToSection('nosotros')} className="nav-link">Nosotros</button>
              <button onClick={() => scrollToSection('tecnologias')} className="nav-link">Tecnologías</button>
              <button onClick={() => scrollToSection('portafolio')} className="nav-link">Portafolio</button>
              <button onClick={() => scrollToSection('testimonios')} className="nav-link">Testimonios</button>
              <Button onClick={() => window.open('https://wa.me/2411222257', '_blank')} className="nav-cta">
                Contactar
              </Button>
            </div>

            <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-gradient"></div>
        <div className="container-custom">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <Zap className="w-4 h-4" />
                <span>Innovación y Tecnología Premium</span>
              </div>
              <h1 className="hero-title">
                Desarrollo de Software y Apps <span className="gradient-text">a la Medida</span>
              </h1>
              <p className="hero-subtitle">
                Creamos soluciones digitales modernas, rápidas y escalables para impulsar tu negocio.
              </p>
              <div className="hero-cta">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="lg" className="cta-primary">
                      Solicitar Cotización
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dialog-content">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">Solicitar Cotización</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="name">Nombre Completo</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Teléfono</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="message">Cuéntanos sobre tu proyecto</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                      <Button type="submit" className="w-full cta-primary" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                  Enviando...
                          </>
                        ) : (
                          <>
                                  Enviar Solicitud
                            <MessageCircle className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button
                  size="lg"
                  variant="outline"
                  className="cta-secondary"
                  onClick={() => window.open('https://wa.me/2411222257', '_blank')}
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Hablar por WhatsApp
                </Button>
              </div>
            </div>
            <div className="hero-mockups">
              <div className="mockup-card mockup-1">
                <img src={mockData.heroImages[0]} alt="Dashboard" />
              </div>
              <div className="mockup-card mockup-2">
                <img src={mockData.heroImages[1]} alt="Mobile App" />
              </div>
              <div className="mockup-card mockup-3">
                <img src={mockData.heroImages[2]} alt="Interface" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="section-padding">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Nuestros Servicios</h2>
            <p className="section-subtitle">Soluciones tecnológicas completas para tu negocio</p>
          </div>
          <div className="services-grid">
            {mockData.services.map((service, index) => (
              <Card key={index} className="service-card">
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="section-padding about-section">
        <div className="container-custom">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">Sobre Nosotros</h2>
              <p className="about-description">
                En <span className="gradient-text">Código a la Medida</span> desarrollamos soluciones digitales personalizadas para empresas y emprendedores. Transformamos ideas en plataformas modernas, eficientes y escalables.
              </p>
              <p className="about-description">
                Nuestro equipo de expertos combina creatividad, tecnología de punta y metodologías ágiles para entregar proyectos que superan expectativas.
              </p>
            </div>
            <div className="stats-grid">
              {mockData.stats.map((stat, index) => (
                <Card key={index} className="stat-card">
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-number">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="tecnologias" className="section-padding">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Tecnologías que Dominamos</h2>
            <p className="section-subtitle">Stack tecnológico moderno y escalable</p>
          </div>
          <div className="tech-grid">
            {mockData.technologies.map((tech, index) => (
              <div key={index} className="tech-badge">
                <span>{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portafolio" className="section-padding portfolio-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Proyectos Destacados</h2>
            <p className="section-subtitle">Soluciones que transforman negocios</p>
          </div>
          <div className="portfolio-grid">
            {mockData.portfolio.map((project, index) => (
              <Card key={index} className="portfolio-card">
                <div className="portfolio-image">
                  <img src={project.image} alt={project.title} />
                  <div className="portfolio-overlay">
                    <Button className="portfolio-btn">Ver Detalles</Button>
                  </div>
                </div>
                <div className="portfolio-content">
                  <h3 className="portfolio-title">{project.title}</h3>
                  <p className="portfolio-description">{project.description}</p>
                  <div className="portfolio-tags">
                    {project.tags.map((tag, idx) => (
                      <span key={idx} className="portfolio-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="section-padding">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Lo que Dicen Nuestros Clientes</h2>
            <p className="section-subtitle">Testimonios reales de proyectos exitosos</p>
          </div>
          <div className="testimonials-grid">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="star-icon" fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.name.charAt(0)}</div>
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-position">{testimonial.position}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="container-custom">
          <div className="cta-content">
            <Rocket className="cta-icon" />
            <h2 className="cta-title">¿Listo para transformar tu negocio con tecnología?</h2>
            <p className="cta-description">
              Contáctanos hoy y descubre cómo podemos ayudarte a alcanzar tus objetivos digitales
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => window.open('https://wa.me/2411222257', '_blank')}
            >
              <MessageCircle className="mr-2 w-5 h-5" />
              Comenzar Ahora
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-custom">
          <div className="footer-content">
            <div className="footer-column">
              <div className="footer-logo">
                <Code className="w-8 h-8" />
                <span className="text-xl font-bold">Código a la Medida</span>
              </div>
              <p className="footer-description">
                Desarrollamos soluciones digitales que impulsan tu negocio hacia el futuro.
              </p>
            </div>
            <div className="footer-column">
              <h4 className="footer-heading">Contacto</h4>
              <ul className="footer-list">
                <li>Yahuquemehcan, Tlaxcala</li>
                <li>México</li>
                <li>+52 241 122 2257</li>
                <li>
                  <a href={`mailto:${mockData.contactEmail}`}>{mockData.contactEmail}</a>
                </li>
              </ul>
            </div>
            <div className="footer-column">
              <h4 className="footer-heading">Servicios</h4>
              <ul className="footer-list">
                <li>Desarrollo Web</li>
                <li>Apps Móviles</li>
                <li>Software Empresarial</li>
                <li>Automatización</li>
              </ul>
            </div>
            <div className="footer-column">
              <h4 className="footer-heading">Síguenos</h4>
              <div className="social-links">
                <a 
                  href="https://www.facebook.com/profile.php?id=61590033047777" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Código a la Medida. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button 
        className="whatsapp-float"
        onClick={() => window.open('https://wa.me/2411222257', '_blank')}
        aria-label="Contactar por WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
};

export default Home;